import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import {
  useAdminGroups,
  useCreateGroup,
  useDeleteGroup,
} from "@/hooks/use-groups";
import { useAllExams } from "@/hooks/use-exams";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createErrorMessage } from "@/lib/error-message";

export const Route = createFileRoute("/_main/admin_/groups")({
  component: AdminGroupsPage,
});

function AdminGroupsPage() {
  const { data: groups = [], isLoading } = useAdminGroups();
  const deleteGroup = useDeleteGroup();
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState("");

  async function handleDelete(gid: string, name: string) {
    setError("");
    if (!confirm(`그룹 "${name}"을(를) 삭제할까요? 모든 멤버와 초대가 함께 사라집니다.`))
      return;
    try {
      await deleteGroup.mutateAsync(gid);
    } catch (err) {
      setError(createErrorMessage(err));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-2">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            관리자
          </Link>
          <h1 className="text-2xl font-bold">그룹 관리</h1>
          <p className="text-sm text-muted-foreground">
            새 그룹을 만들고, 기존 그룹으로 이동해 멤버와 초대를 관리합니다.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />새 그룹
        </Button>
      </div>

      {error && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <Users className="size-10 text-muted-foreground" />
            <p className="font-medium">아직 만든 그룹이 없습니다</p>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />첫 그룹 만들기
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <Card key={g.id}>
              <CardHeader>
                <CardTitle className="line-clamp-1">{g.name}</CardTitle>
                {g.description && (
                  <CardDescription className="line-clamp-2">
                    {g.description}
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent className="flex flex-col gap-3 text-sm">
                <div className="flex flex-wrap gap-1">
                  {g.exams.slice(0, 4).map((s) => (
                    <Badge key={s} variant="secondary" className="text-[11px]">
                      {s}
                    </Badge>
                  ))}
                  {g.exams.length > 4 && (
                    <Badge variant="outline" className="text-[11px]">
                      +{g.exams.length - 4}
                    </Badge>
                  )}
                </div>
                <div className="mt-2 flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(g.id, g.name)}
                    disabled={deleteGroup.isPending}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                  <Link to="/groups/$gid" params={{ gid: g.id }}>
                    <Button size="sm">관리하기</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function CreateGroupDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data: exams = [] } = useAllExams();
  const create = useCreateGroup();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");

  const exam_ids = useMemo(
    () => exams.map((e) => ({ id: e.id, name: e.name })),
    [exams],
  );

  function reset() {
    setName("");
    setDescription("");
    setSelected(new Set());
    setError("");
  }

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    setError("");
    if (!name.trim()) {
      setError("그룹 이름을 입력해주세요");
      return;
    }
    if (selected.size === 0) {
      setError("랭킹에 포함할 시험을 1개 이상 선택해주세요");
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        description: description.trim(),
        exams: Array.from(selected),
      });
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(createErrorMessage(err));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>새 그룹 만들기</DialogTitle>
          <DialogDescription>
            그룹은 사이트 관리자만 만들 수 있습니다. 시험 추가는 추후 디스코드
            문의로 처리됩니다.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="g-name">이름</Label>
            <Input
              id="g-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 5월 SAA 스터디"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="g-desc">설명 (선택)</Label>
            <textarea
              id="g-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="rounded-lg border bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>랭킹에 포함할 시험</Label>
            <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg border p-2">
              {exam_ids.length === 0 ? (
                <p className="px-2 py-1 text-sm text-muted-foreground">
                  시험이 없습니다
                </p>
              ) : (
                exam_ids.map((e) => (
                  <label
                    key={e.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(e.id)}
                      onChange={() => toggle(e.id)}
                      className="size-4 cursor-pointer"
                    />
                    <span className="text-sm">{e.name}</span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {e.id}
                    </span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {selected.size}개 선택됨
            </p>
          </div>
          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="size-4 animate-spin" />}
            만들기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
